import type {
  CoviChatItem,
  CoviToolStep,
  CoviTurnContext,
} from "../model/types"
import { buildActionTitle, type DroppedProposal } from "./actions"
import { boundHistory } from "./history"
import { isPythonMetricAction } from "./pythonMetricReceipt"
import { toolLabel, toolState } from "./toolSteps"
import { boundToolTrace } from "./toolTraceBounds"
import { snapshotCoviViewContext } from "./viewContext"

const sessionSeed = Math.random().toString(36).slice(2, 8)
let itemCounter = 0

/** Cap on one serialized outcome clause — this rides every subsequent prompt. */
const MAX_OUTCOME_CHARS = 160
// Two bounded 128-character identities plus fixed receipt wording must survive.
const MAX_PYTHON_METRIC_OUTCOME_CHARS = 512
const MAX_REPORTED_DROPPED_PROPOSALS = 4
const MAX_DROPPED_KIND_CHARS = 64
const MAX_DROPPED_REASON_CHARS = 120

/** Charter-coupled wording — read actionOutcome before touching it. */
const STAGED_OUTCOME = "staged in the editor, not yet saved"
/** Tail of the test-set editor bridge's receipt, e.g. "2 test cases staged for review". */
const STAGED_DETAIL_SUFFIX = /\s*staged(?:\s+for\s+review)?$/i
/** Resource ids carry a capital, digit, or underscore; bare route roots do not. */
const RESOURCE_ID_SHAPE = /[A-Z0-9_]/

interface ChatHistoryMessage {
  role: string
  content: string
}

export function nextChatItemId(prefix: string): string {
  itemCounter += 1
  return `${prefix}_${sessionSeed}_${itemCounter}`
}

function clampOutcome(text: string, limit: number): string {
  const collapsed = text.trim().replace(/\s+/g, " ")
  return collapsed.length > limit ? `${collapsed.slice(0, limit)}…` : collapsed
}

/** Created-resource id from a receipt deep link, when the link points at one. */
function resourceIdFromHref(href: string | undefined): string | null {
  if (!href) {
    return null
  }
  const lastSegment = href.split(/[?#]/)[0].split("/").filter(Boolean).at(-1)
  return lastSegment && RESOURCE_ID_SHAPE.test(lastSegment) ? lastSegment : null
}

/**
 * Outcome clause for a settled action. Staged editor changes read differently
 * from executed ones on purpose: nothing is written until the user saves, so
 * reporting them as "done" is what makes Sofia re-propose work it already did.
 */
function settledOutcome(
  item: Extract<CoviChatItem, { kind: "action" }>,
): string {
  // Classify before clamping: the clamp cuts the tail the staged test reads.
  const fullDetail = (item.detail ?? "").trim().replace(/\s+/g, " ")
  if (STAGED_DETAIL_SUFFIX.test(fullDetail)) {
    const staged = clampOutcome(
      fullDetail.replace(STAGED_DETAIL_SUFFIX, ""),
      MAX_OUTCOME_CHARS,
    )
    return staged.length > 0 ? `${STAGED_OUTCOME}: ${staged}` : STAGED_OUTCOME
  }
  const detail = clampOutcome(
    fullDetail,
    isPythonMetricAction(item.proposal.kind)
      ? MAX_PYTHON_METRIC_OUTCOME_CHARS
      : MAX_OUTCOME_CHARS,
  )
  const resourceId = resourceIdFromHref(item.href)
  const withId =
    resourceId && !detail.includes(resourceId)
      ? [detail, `(${resourceId})`].filter(Boolean).join(" ")
      : detail
  return withId.length > 0 ? `done: ${withId}` : "done"
}

/**
 * The staged / `done:` / `failed:` clauses are substring-matched by the agent
 * charter to tell a landed write from an unsaved one. Rewording one silently
 * stops that rule firing, and nothing on either side fails.
 */
function actionOutcome(
  item: Extract<CoviChatItem, { kind: "action" }>,
): string {
  switch (item.status) {
    case "proposed":
      return "awaiting confirmation"
    case "executing":
      return "in progress"
    case "dismissed":
      return "dismissed by the user"
    case "error": {
      const reason = clampOutcome(item.error ?? "", MAX_OUTCOME_CHARS)
      // An expired confirmation is not retryable — only a fresh proposal is.
      const expiry =
        item.expired === true ? " (confirmation expired, re-propose)" : ""
      return reason.length > 0
        ? `failed: ${reason}${expiry}`
        : `failed${expiry}`
    }
    case "done":
      return settledOutcome(item)
  }
}

function buildActionHistoryEntry(
  item: Extract<CoviChatItem, { kind: "action" }>,
): string {
  const rationale = item.proposal.rationale.trim()
  const summary = `Proposed action: ${buildActionTitle(item.proposal)} — ${actionOutcome(item)}`
  return rationale.length > 0 ? `${summary}\nReason: ${rationale}` : summary
}

function buildVisualizationHistoryEntry(
  item: Extract<CoviChatItem, { kind: "action" }>,
  includeSpec: boolean,
): string {
  const rawTitle = item.proposal.args.title
  const title =
    typeof rawTitle === "string" && rawTitle.trim().length > 0
      ? rawTitle.trim()
      : "chart"
  const summary = `Rendered visualization: ${title}`
  if (!includeSpec) {
    return summary
  }
  let spec = ""
  try {
    spec = JSON.stringify(item.proposal.args.spec) ?? ""
  } catch {
    spec = ""
  }
  return spec.length > 0
    ? `${summary}\nSpec:\n\`\`\`json\n${spec}\n\`\`\``
    : summary
}

export function buildToolItems(steps: CoviToolStep[]): CoviChatItem[] {
  return boundToolTrace(steps).map(
    (step): CoviChatItem => ({
      kind: "tool",
      id: nextChatItemId("tool"),
      name: step.name,
      summary: toolLabel(step.name, step),
      omittedSteps: step.omittedSteps,
      traceTruncated: step.traceTruncated,
      activity: step.activity,
      resource_kind: step.resource_kind,
      state: step.state,
      status: step.status,
      running: toolState(step) === "running",
      isError: toolState(step) === "failed",
      isResearch: step.is_research,
    }),
  )
}

export function conversationIdForItems(items: CoviChatItem[]): string | null {
  const firstItemId = items[0]?.id
  return firstItemId ? `conv-${firstItemId}`.slice(0, 128) : null
}

/**
 * Proposals the proxy refused, awaiting their one report to Sofia. Keyed by
 * conversation: several threads can be resident at once, and a refusal one
 * thread reported must not ride out on another thread's turn. Client-only, so
 * it never spans users.
 */
const pendingDroppedProposals = new Map<string, DroppedProposal[]>()
/** Ceiling on queued conversations — a thread that never sends again must not pin entries forever. */
const MAX_PENDING_CONVERSATIONS = 64

/**
 * Stash the refusals a completion reported, bounded and de-duplicated per
 * conversation. Read off the wire, so every field is re-checked rather than
 * trusted.
 */
export function recordDroppedProposals(
  conversationId: string,
  value: unknown,
): void {
  if (!Array.isArray(value)) {
    return
  }
  let queue = pendingDroppedProposals.get(conversationId)
  if (!queue) {
    if (pendingDroppedProposals.size >= MAX_PENDING_CONVERSATIONS) {
      const oldest = pendingDroppedProposals.keys().next().value
      if (oldest !== undefined) {
        pendingDroppedProposals.delete(oldest)
      }
    }
    queue = []
    pendingDroppedProposals.set(conversationId, queue)
  }
  for (const entry of value) {
    if (queue.length >= MAX_REPORTED_DROPPED_PROPOSALS) {
      return
    }
    if (typeof entry !== "object" || entry === null) {
      continue
    }
    const { kind, reason } = entry as { kind?: unknown; reason?: unknown }
    if (typeof kind !== "string" || typeof reason !== "string") {
      continue
    }
    const drop: DroppedProposal = {
      kind: clampOutcome(kind, MAX_DROPPED_KIND_CHARS),
      reason: clampOutcome(reason, MAX_DROPPED_REASON_CHARS),
    }
    if (drop.kind.length === 0 || drop.reason.length === 0) {
      continue
    }
    const duplicate = queue.some(
      (pending) => pending.kind === drop.kind && pending.reason === drop.reason,
    )
    if (!duplicate) {
      queue.push(drop)
    }
  }
}

/** Turn context posted to the proxy; free-form beyond the fields set here. */
interface CoviTurnContextPayload extends Record<string, unknown> {
  dropped_proposals?: DroppedProposal[]
  action_outcomes?: ActionOutcomeReport[]
}

interface ActionOutcomeReport {
  item_id: string
  proposal_id: string
  kind: string
  status:
    | "pending"
    | "executing"
    | "staged"
    | "completed"
    | "failed"
    | "dismissed"
  title: string
  outcome: string
}

/** Keep attempts separate: proposal ids identify arguments, so identical actions can have different outcomes. */
function actionOutcomeReports(
  items: CoviChatItem[],
  priorityActionItemId?: string,
): ActionOutcomeReport[] {
  const latest = new Map<
    string,
    {
      item: Extract<CoviChatItem, { kind: "action" }>
      report: ActionOutcomeReport
    }
  >()
  for (const item of items) {
    if (item.kind !== "action" || item.provisional === true) {
      continue
    }
    const proposalId = item.proposal.proposal_id
    if (
      !proposalId ||
      proposalId.trim() !== proposalId ||
      proposalId.length > 128 ||
      !item.id ||
      item.id.trim() !== item.id ||
      item.id.length > 128
    ) {
      continue
    }
    const outcome = actionOutcome(item)
    const status: ActionOutcomeReport["status"] =
      item.status === "done"
        ? outcome.startsWith(STAGED_OUTCOME)
          ? "staged"
          : "completed"
        : item.status === "error"
          ? "failed"
          : item.status === "proposed"
            ? "pending"
            : item.status
    latest.delete(item.id)
    latest.set(item.id, {
      item,
      report: {
        item_id: item.id,
        proposal_id: proposalId,
        kind: item.proposal.kind,
        status,
        title: clampOutcome(buildActionTitle(item.proposal), 160).slice(0, 160),
        outcome: outcome.slice(0, 512),
      },
    })
  }
  // Keep in-flight work visible; terminal-card recency follows its existing settled timestamp.
  const recent = [...latest.values()].sort(
    (left, right) =>
      Number(left.item.status === "executing") -
        Number(right.item.status === "executing") ||
      (left.item.settledAt ?? left.item.proposedAt) -
        (right.item.settledAt ?? right.item.proposedAt),
  )
  if (priorityActionItemId !== undefined) {
    const index = recent.findIndex(
      ({ report }) => report.item_id === priorityActionItemId,
    )
    if (index >= 0) {
      const [priority] = recent.splice(index, 1)
      recent.push(priority)
    }
  }
  // Lockstep with Sofia's MAX_ACTION_OUTCOMES; omitted actions have unknown state.
  return recent.slice(-8).map(({ report }) => report)
}

/**
 * Discard the refusals a landed send carried, identified by the context it
 * actually posted. A send that fails skips this so the next one retries the
 * report instead of dropping it. Clearing by carried entry rather than by
 * conversation is what keeps an overlapping send — nothing serializes them —
 * from deleting a refusal recorded after its own payload was built. Called
 * with no argument, everything goes (test reset).
 */
export function clearRecordedDroppedProposals(
  conversationId?: string,
  sentContext?: CoviTurnContextPayload,
): void {
  if (conversationId === undefined) {
    pendingDroppedProposals.clear()
    return
  }
  const queue = pendingDroppedProposals.get(conversationId)
  if (!queue) {
    return
  }
  const delivered = new Set(sentContext?.dropped_proposals)
  const remaining = queue.filter((entry) => !delivered.has(entry))
  if (remaining.length === 0) {
    pendingDroppedProposals.delete(conversationId)
    return
  }
  // Overwriting the value keeps the key's insertion position, so the
  // oldest-conversation eviction order survives a partial clear.
  pendingDroppedProposals.set(conversationId, remaining)
}

export function buildTurnContextPayload(
  context: CoviTurnContext,
  conversationId: string,
  items: CoviChatItem[] = [],
  priorityActionItemId?: string,
): CoviTurnContextPayload {
  const payload: CoviTurnContextPayload = {
    conversation_id: conversationId,
    // Advertise from the loaded browser bundle, including confirmation turns.
    alert_actions_version: 1,
    python_metrics_version: 1,
    python_analysis_metric_save_version: 1,
    // This bundle stages one update_test_cases batch in the test-set editor;
    // sofia-agent offers that kind only to a client that says so (COVAL-7496).
    test_case_batch_edits_version: 1,
    // It also applies a batch entry's append_input_str (text added after the
    // case's input), so Sofia need not re-send whole inputs to append.
    test_case_appends_version: 1,
    selected_run_launch_version: 1,
    test_set_creation_version: 1,
  }
  // Attached here but kept until the caller confirms the turn landed
  // (clearRecordedDroppedProposals): a cancelled, refused, or failed send
  // retries the report instead of silently losing it. Still reported exactly
  // once — the clear after a landed turn is what stops a single bad turn from
  // riding along in every prompt that follows it. The entries handed out are
  // the queued objects themselves, so the clear can match on identity.
  const pending = pendingDroppedProposals.get(conversationId)
  if (pending && pending.length > 0) {
    payload.dropped_proposals = [...pending]
  }
  const outcomes = actionOutcomeReports(items, priorityActionItemId)
  if (outcomes.length > 0) {
    payload.action_outcomes = outcomes
  }
  if (context.page) {
    payload.page = context.page
  }
  if (context.onboardingCounts) {
    payload.onboardingCounts = context.onboardingCounts
  }
  const { view, focus_stack: focusStack } = snapshotCoviViewContext()
  if (view) {
    payload.view = view
  }
  if (focusStack.length > 0) {
    payload.focus_stack = focusStack
  }
  return payload
}

export function buildChatHistory(items: CoviChatItem[]): ChatHistoryMessage[] {
  let latestVisualizationId: string | null = null
  for (const item of items) {
    if (
      item.kind === "action" &&
      item.proposal.kind === "render_visualization"
    ) {
      latestVisualizationId = item.id
    }
  }

  const history = items.reduce<ChatHistoryMessage[]>((messages, item) => {
    if (item.provisional === true) {
      return messages
    }
    if (
      (item.kind === "user" || item.kind === "assistant") &&
      item.text.trim().length > 0
    ) {
      messages.push({
        role: item.kind === "user" ? "user" : "assistant",
        content: item.text,
      })
      return messages
    }
    if (item.kind !== "action") {
      return messages
    }

    const serialized =
      item.proposal.kind === "render_visualization"
        ? buildVisualizationHistoryEntry(
            item,
            item.id === latestVisualizationId,
          )
        : buildActionHistoryEntry(item)
    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === "assistant") {
      lastMessage.content = `${lastMessage.content}\n\n${serialized}`
    } else {
      messages.push({ role: "assistant", content: serialized })
    }
    return messages
  }, [])
  return boundHistory(history)
}

/** Render a buffered reply only when it contains text; analysis-only turns use their card. */
export function buildReplyItems(content: string): CoviChatItem[] {
  return content.trim().length > 0
    ? [{ kind: "assistant", id: nextChatItemId("a"), text: content }]
    : []
}
